/**
 * Web3 Provider
 *
 * Sets up Reown AppKit with both EVM (wagmi) and Solana adapters.
 * Provides wallet connection across 500+ wallets with mobile support.
 */

import { ReactNode } from 'react';
import { createAppKit } from '@reown/appkit/react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { SolanaAdapter } from '@reown/appkit-adapter-solana';
import {
  mainnet,
  arbitrum,
  polygon,
  optimism,
  base,
  bsc,
  avalanche,
  linea,
  solana,
  type AppKitNetwork,
} from '@reown/appkit/networks';

import { WALLETCONNECT_PROJECT_ID } from '../config/walletConfig';

// Query client for React Query (used by wagmi)
const queryClient = new QueryClient();

// Define custom chains
const blast: AppKitNetwork = {
  id: 81457,
  name: 'Blast',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc.blast.io'] },
  },
  blockExplorers: {
    default: { name: 'Blastscan', url: 'https://blastscan.io' },
  },
  chainNamespace: 'eip155',
};

const zkSyncNetwork: AppKitNetwork = {
  id: 324,
  name: 'zkSync Era',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://mainnet.era.zksync.io'] },
  },
  blockExplorers: {
    default: { name: 'zkSync Explorer', url: 'https://explorer.zksync.io' },
  },
  chainNamespace: 'eip155',
};

// Define all supported networks
const evmNetworks: [AppKitNetwork, ...AppKitNetwork[]] = [
  mainnet,
  polygon,
  arbitrum,
  optimism,
  base,
  bsc,
  avalanche,
  linea,
  blast,
  zkSyncNetwork,
];

// All networks combined (EVM + Solana)
const allNetworks: [AppKitNetwork, ...AppKitNetwork[]] = [
  mainnet,
  polygon,
  arbitrum,
  optimism,
  base,
  bsc,
  avalanche,
  linea,
  blast,
  zkSyncNetwork,
  solana,
];

// Get current origin for metadata
const getOrigin = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://joinkanji.com';
};

// App metadata for WalletConnect
const metadata = {
  name: 'Kanji',
  description: 'Kanji Testnet - Claim your spot',
  url: getOrigin(),
  icons: ['https://joinkanji.com/favicon.ico'],
};

// Create Wagmi adapter for EVM chains
const wagmiAdapter = new WagmiAdapter({
  projectId: WALLETCONNECT_PROJECT_ID,
  networks: evmNetworks,
});

// Create Solana adapter (uses AppKit's built-in wallet discovery)
const solanaAdapter = new SolanaAdapter();

// Initialize AppKit modal (must be outside component)
createAppKit({
  adapters: [wagmiAdapter, solanaAdapter],
  networks: allNetworks,
  defaultNetwork: mainnet,
  projectId: WALLETCONNECT_PROJECT_ID,
  metadata,
  features: {
    analytics: true,
    email: false,
    socials: false,
  },
  // Feature wallets to show first (helps avoid SVG icon issues with unknown wallets)
  featuredWalletIds: [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
    'a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393', // Phantom
    '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
    '1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369', // Rainbow
    'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa', // Coinbase
  ],
  themeMode: 'dark',
  themeVariables: {
    '--w3m-color-mix': '#A371E9',
    '--w3m-accent': '#A371E9',
    '--w3m-border-radius-master': '4px',
  },
});

// Export wagmi config for hooks
export const wagmiConfig = wagmiAdapter.wagmiConfig;

interface Web3ProviderProps {
  children: ReactNode;
}

export function Web3Provider({ children }: Web3ProviderProps) {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </WagmiProvider>
  );
}
